/**
 * The All News page body (`/about-us/news-events/news/`): every article as a card (category
 * badge, title linking to `/about-us/news-events/news/<categorySlug>/<slug>`, publish date as
 * `MM/DD/YYYY`), with category tabs and a "Load More" button.
 *
 * Rendered only by `src/pages/about-us/news-events/news/index.tsx`, with the copy from
 * `src/content/ryerson/about-us/news-events/news/index.ts` and the newest-first list from
 * `src/contentful/news/getContentfulNewsForSections.ts`.
 *
 * Layout: from `lg` up, a row of one wide and two narrow cards, a row of four, the newsletter
 * sign-up, then cards up to the 15th, with "Load More" adding eight. Below `lg`, six cards, the
 * newsletter sign-up, then cards up to the 10th, with "Load More" adding four.
 *
 * Rules a change must keep: a tab's `url` is compared with each article's `categorySlug`, so the
 * tabs' `url` values must be the slugs from `src/contentful/news/helpers/index.ts` →
 * `getNewsCategorySlugFromCategoryLabel`; `""` is "All News".
 *
 * As the code stands:
 * - The list is copied into state at mount and refreshed only when a tab is picked, so after a
 *   language switch the cards stay in the previous language until then.
 * - "Load More" shows only while more than one article is hidden: with exactly one left (for
 *   example 16 articles on desktop, 11 on mobile), the last article is never shown.
 */
import React from "react";
import { useApplication } from "@ryerson/frontend.application";
import { ContentSection } from "@ryerson/frontend.layout";
import { Badge } from "@ryerson/frontend.notification";
import { Link } from "@ryerson/frontend.navigation";
import { Breadcrumb } from "@ryerson/frontend.navigation";
import { Typography } from "@ryerson/frontend.typography";
import { Media } from "@ryerson/frontend.next";
import { useTheme } from "@ryerson/frontend.theme";
import styled from "@emotion/styled";
import { Icon } from "@ryerson/frontend.assets";
import { css } from "@emotion/react";
import { NewsBadgeColor, MultiLingualNewsCategories, NewsCategories } from "./NewsArticle";
import StayTunedComponent from "@components/Shared/StayTuned/StayTuned";
import { ProcessedContentfulNewsForSectionsByLanguage } from "@contentful/news/types";
import Spacer from "@components/Shared/Spacer/Spacer";
/** One category tab. `url` is the URL category slug it filters on, `""` for every article. */
export type NewsCategory = {
	label: string;
	url: string;
};

/**
 * Copy of the All News page, filled by `src/content/ryerson/about-us/news-events/news/index.ts`.
 */
export type NewsLandingStaticContent = {
	title: string;
	backLabel: string;
	backUrl: string;
	loadMore: string;
	categories: NewsCategory[];
};

const BreadcrumbContainer = styled.div`
	margin-top: 4px;
	${(props: any) => {
		const { theme } = props;
		return css`
			@media only screen and (max-width: ${theme.breakpoints.lg}) {
				margin-top: 8px;
			}
		`;
	}}
`;

const NavContainer = styled.div`
	display: block;
	width: 100%;
	height: 58px;
	box-sizing: border-box;
`;

const NavItem = styled.div`
	display: inline-block;
	height: 58px;
	line-height: 57px;
	cursor: pointer;
	width: auto;
`;

const RelativeContentSection = styled(ContentSection)`
	position: relative;
`;

const Divider = styled.div`
	display: block;
	width: 100%;
	height: 1px;
	margin: 0px;
`;

const NewsItem = styled.div`
	width: calc(25% - 7.5px);
	margin-right: 10px;
	height: 320px;
	display: inline-block;
	margin-bottom: 10px;
	border-radius: 2px;
	vertical-align: top;
	padding: 30px 40px 36px 40px;
	box-sizing: border-box;
	position: relative;
`;

const MobileNewsItem = styled.div`
	width: 100%;
	height: 220px;
	display: block;
	margin-bottom: 20px;
	border-radius: 2px;
	padding: 17px 20px;
	box-sizing: border-box;
	position: relative;
`;

const LoadMore = styled.div`
	display: block;
	margin: 0 auto;
	cursor: pointer;
	width: 210px;
	box-sizing: border-box;
	line-height: 36px;
	text-align: center;
	height: 40px;
	border-radius: 24px;
`;

const RevealBox = styled.div`
	width: calc(100% - 40px);
	position: absolute;
	top: 60px;
	left: 20px;
	height: 0px;
	overflow: hidden;
	padding: 0px 20px;
	box-sizing: border-box;
	box-shadow: 0px 10px 80px rgb(0 0 0 / 7%), 0px 10px 17.869px rgb(0 0 0 / 4%),
		0px 5px 5.32008px rgb(0 0 0 / 3%);
	background-color: #ffffff;
	z-index: 1;
`;

const MobileDivider = styled.hr`
	display: block;
	width: 100%;
	opacity: 0.1;
`;

const CategorySelector = styled.span`
	display: inline-block;
`;

// The GA `stay_tuned` event value is "News-Events", or "News-Events_<categorySlug>" while a tab
// is picked (see `Shared/StayTuned/StayTuned.tsx`).
const stayTunedParent = "News-Events";

type AllNewsProps = {
	content: NewsLandingStaticContent;
	contentfulNewsByLanguage: ProcessedContentfulNewsForSectionsByLanguage;
};

/** Renders the All News page body; see the file header for the layout and paging. */
const AllNews: React.FC<AllNewsProps> = ({ content, contentfulNewsByLanguage }) => {
	const {
		localization: { language },
	} = useApplication();

	const allNewsForLanguage = contentfulNewsByLanguage[language];
	const [currentNews, setCurrentNews] = React.useState(allNewsForLanguage);
	const [filter, setFilter] = React.useState<string>("");
	const [slicePoint, setSlicePoint] = React.useState<number>(15);
	const [mobileSlicePoint, setMobileSlicePoint] = React.useState<number>(10);
	const [showMobileNav, setShowMobileNav] = React.useState<boolean>(false);

	// Runs on mount and when a tab is picked, not on a language change. Picking a category resets
	// the paging; picking "All News" keeps it.
	React.useEffect(() => {
		if (filter === "") {
			setCurrentNews(allNewsForLanguage);
		} else {
			setSlicePoint(15);
			setMobileSlicePoint(10);
			setCurrentNews(allNewsForLanguage.filter((item) => item.categorySlug === filter));
		}
	}, [filter]);

	const { theme } = useTheme();

	// "YYYY-MM-DD" -> "MM/DD/YYYY"; see `./LatestNewsSection.tsx` for the parsing caveat.
	const FormatDate = (date: any) => {
		let dateObject = new Date(date + " 00:00:00");
		let m: any = dateObject.getMonth() + 1;
		let d: any = dateObject.getDate();
		let y: any = dateObject.getFullYear();
		if (m < 10) m = "0" + m;
		if (d < 10) d = "0" + d;
		return m + "/" + d + "/" + y;
	};

	return (
		<>
			<Media greaterThanOrEqual="lg">
				<ContentSection vPadding="48px" type="tertiary">
					<BreadcrumbContainer theme={theme}>
						<Breadcrumb type="tertiary" size="xs" />
					</BreadcrumbContainer>
					<Typography
						variant="h1"
						css={css`
							margin-top: 20px;
							margin-bottom: 0px;
						`}
						type="tertiary"
					>
						{content.title}
						<Typography
							variant="div"
							css={css`
								display: inline-block;
								margin-left: 40px;
								vertical-align: middle;
							`}
							type="tertiary"
							size="sm"
						>
							<Link to={content.backUrl}>
								<Typography
									variant="div"
									css={css`
										border: 1px solid ${theme.colors.primary.darkGray};
										border-radius: 24px;
										height: 24px;
										width: 24px;
										display: inline-block;
										box-sizing: border-box;
										vertical-align: top;
										text-align: center;
										line-height: 26px;
									`}
								>
									<Icon
										icon="chevron-left"
										color={theme.colors.tertiary.header}
										size="xs"
										css={css`
											cursor: pointer;
										`}
									/>
								</Typography>
								<Typography
									variant="div"
									type="tertiary"
									css={css`
										display: inline-block;
										vertical-align: top;
										margin-left: 13px;
										height: 24px;
										line-height: 24px;
									`}
								>
									{content.backLabel}
								</Typography>
							</Link>
						</Typography>
					</Typography>
				</ContentSection>
				<NavContainer
					css={css`
						background-color: ${theme.colors.secondary.background};
						position: relative;
					`}
				>
					<ContentSection vPadding="0px" type="secondary">
						{content.categories.map((category: NewsCategory, index: number) => (
							<NavItem
								key={index}
								css={css`
									margin-right: ${index === content.categories.length - 1
										? "0px"
										: "40px"};
									border-bottom: ${filter === category.url
										? "1px solid " + theme.colors.primary.link
										: "1px solid transparent"};
								`}
								onClick={() => {
									setFilter(category.url);
								}}
							>
								<Typography
									variant="span"
									type="secondary"
									color={theme.colors.secondary.header}
									size="md"
									weight="bold"
									css={css`
										display: block;
										height: 57px;
										line-height: 57px;
									`}
								>
									{category.label}
								</Typography>
							</NavItem>
						))}
					</ContentSection>
					<Divider
						css={css`
							border-bottom: 1px solid ${theme.colors.primary.darkerGray};
							opacity: 0.1;
							position: absolute;
							bottom: 0px;
							left: 0px;
						`}
					/>
				</NavContainer>
				<ContentSection type="secondary" vPadding="80px">
					<Spacer
						css={css`
							height: 40px;
						`}
					/>
					{currentNews.slice(0, 3).map((item, index) => (
						<NewsItem
							key={index}
							css={css`
								width: ${index === 0 ? "calc( 50% - 5px )" : "calc( 25% - 7.5px)"};
								margin-right: ${index === 2 ? "0px" : "10px"};
								background-color: ${theme.colors.primary.background};
							`}
						>
							<Badge
								css={css`
									margin-bottom: 25px;
								`}
								type="primary"
								color={NewsBadgeColor(item.category as NewsCategories)}
							>
								{MultiLingualNewsCategories[language][item.categorySlug]}
							</Badge>
							<Link
								to={
									"/about-us/news-events/news/" +
									encodeURI(item.categorySlug) +
									"/" +
									encodeURI(item.slug)
								}
							>
								<Typography
									variant={index === 0 ? "h3" : "div"}
									weight="bold"
									color={theme.colors.primary.header}
									size="md"
									css={css`
										margin-top: 0px;
										margin-bottom: 0px;
									`}
								>
									{item.title}
								</Typography>
							</Link>
							<Typography
								variant="div"
								size="sm"
								type="primary"
								color={theme.colors.primary.gray}
								css={css`
									position: absolute;
									left: 40px;
									bottom: 36px;
								`}
							>
								{FormatDate(item.publishDate)}
							</Typography>
						</NewsItem>
					))}
					{currentNews.slice(3, 7).map((item, index: number) => {
						return (
							<NewsItem
								key={index}
								css={css`
									margin-right: ${index === 3 ? "0px" : "10px"};
									background-color: ${theme.colors.primary.background};
								`}
							>
								<Badge
									css={css`
										margin-bottom: 25px;
									`}
									type="primary"
									color={NewsBadgeColor(item.category as NewsCategories)}
								>
									{MultiLingualNewsCategories[language][item.categorySlug]}
								</Badge>
								<Link
									to={
										"/about-us/news-events/news/" +
										encodeURI(item.categorySlug) +
										"/" +
										encodeURI(item.slug)
									}
								>
									<Typography
										variant="div"
										weight="bold"
										color={theme.colors.primary.header}
										size="md"
										css={css`
											margin-top: 0px;
											margin-bottom: 0px;
										`}
									>
										{item.title}
									</Typography>
								</Link>
								<Typography
									variant="div"
									size="sm"
									type="primary"
									color={theme.colors.primary.gray}
									css={css`
										position: absolute;
										left: 40px;
										bottom: 36px;
									`}
								>
									{FormatDate(item.publishDate)}
								</Typography>
							</NewsItem>
						);
					})}
				</ContentSection>
				<StayTunedComponent parent={stayTunedParent} parentCategory={filter} />
				<ContentSection type="secondary" vPadding="80px">
					{currentNews.slice(7, slicePoint).map((item, index: number) => {
						return (
							<React.Fragment key={index}>
								<NewsItem
									css={css`
										margin-right: ${(index + 1) % 4 === 0 ? "0px" : "10px"};
										background-color: ${theme.colors.primary.background};
									`}
								>
									<Badge
										css={css`
											margin-bottom: 25px;
										`}
										type="primary"
										color={NewsBadgeColor(item.category as NewsCategories)}
									>
										{MultiLingualNewsCategories[language][item.categorySlug]}
									</Badge>
									<Link
										to={
											"/about-us/news-events/news/" +
											encodeURI(item.categorySlug) +
											"/" +
											encodeURI(item.slug)
										}
									>
										<Typography
											variant="div"
											weight="bold"
											color={theme.colors.primary.header}
											size="md"
											css={css`
												margin-top: 0px;
												margin-bottom: 0px;
											`}
										>
											{item.title}
										</Typography>
									</Link>
									<Typography
										variant="div"
										size="sm"
										type="primary"
										color={theme.colors.primary.gray}
										css={css`
											position: absolute;
											left: 40px;
											bottom: 36px;
										`}
									>
										{FormatDate(item.publishDate)}
									</Typography>
								</NewsItem>
								{(index + 1) % 8 === 0 ? (
									<Spacer
										css={css`
											height: 70px;
										`}
									/>
								) : (
									<></>
								)}
							</React.Fragment>
						);
					})}
					{/* `slicePoint` cards are showing; this hides the button when exactly one
					is left. */}
					{currentNews.length - 1 > slicePoint ? (
						<LoadMore
							css={css`
								border: 1px solid ${theme.colors.primary.gray};
							`}
							onClick={() => {
								setSlicePoint(slicePoint + 8);
							}}
						>
							<Typography
								variant="span"
								color={theme.colors.primary.darkGray}
								weight="bold"
								size="sm"
							>
								{content.loadMore}
							</Typography>
						</LoadMore>
					) : (
						<></>
					)}
					<Spacer
						css={css`
							height: 80px;
						`}
					/>
				</ContentSection>
			</Media>
			<Media lessThan="lg">
				<ContentSection vPadding="20px" type="tertiary">
					<BreadcrumbContainer theme={theme}>
						<Breadcrumb type="tertiary" size="xs" />
					</BreadcrumbContainer>
					<Typography
						variant="h1"
						css={css`
							margin-top: 12px;
							margin-bottom: 0px;
						`}
						type="tertiary"
					>
						{content.title}
					</Typography>
				</ContentSection>
				<RelativeContentSection type="primary" vPadding="0px">
					<Typography
						variant="div"
						size="lg"
						css={css`
							text-align: right;
							height: 60px;
							line-height: 60px;
						`}
						color={theme.colors.primary.link}
					>
						{content.categories.map((category: NewsCategory, index: number) => {
							return (
								<React.Fragment key={index}>
									{category.url === filter ? (
										<CategorySelector
											onClick={() => {
												setShowMobileNav(showMobileNav ? false : true);
											}}
										>
											{category.label}
											<Icon
												icon="arrow-down"
												css={css`
													cursor: pointer;
													margin-left: 8px;
												`}
												size="xs"
												color={theme.colors.primary.link}
											/>
										</CategorySelector>
									) : (
										<></>
									)}
								</React.Fragment>
							);
						})}
					</Typography>
					<RevealBox
						css={css`
							background-color: ${theme.colors.primary.background};
							height: ${showMobileNav === true ? "230px" : "0px"};
							transition: height 0.25s ease;
						`}
					>
						<Spacer
							css={css`
								height: 24px;
							`}
						/>
						{content.categories.map((category: NewsCategory, index: number) => (
							<React.Fragment key={index}>
								<Typography
									variant="div"
									color={
										filter === category.url
											? theme.colors.primary.gray
											: theme.colors.primary.header
									}
									size="md"
									weight="bold"
									css={css`
										display: block;
										height: 20px;
										line-height: 20px;
										margin-bottom: 10px;
									`}
								>
									<CategorySelector
										onClick={() => {
											setFilter(category.url);
											setShowMobileNav(showMobileNav ? false : true);
										}}
									>
										{category.label}
									</CategorySelector>
								</Typography>
								{index === content.categories.length - 1 ? (
									<></>
								) : (
									<MobileDivider
										css={css`
											color: ${theme.colors.primary.darkerGray};
											margin-bottom: 10px;
										`}
									/>
								)}
							</React.Fragment>
						))}
					</RevealBox>
					{currentNews.slice(0, 6).map((item, index: number) => {
						return (
							<MobileNewsItem
								key={index}
								css={css`
									background-color: ${theme.colors.secondary.background};
								`}
							>
								<Badge
									css={css`
										margin-bottom: 25px;
									`}
									type="primary"
									color={NewsBadgeColor(item.category as NewsCategories)}
								>
									{MultiLingualNewsCategories[language][item.categorySlug]}
								</Badge>
								<Link
									to={
										"/about-us/news-events/news/" +
										encodeURI(item.categorySlug) +
										"/" +
										encodeURI(item.slug)
									}
								>
									<Typography
										variant={index === 0 ? "h2" : "div"}
										weight="bold"
										color={theme.colors.primary.header}
										size="lg"
										css={css`
											margin-top: 0px;
											margin-bottom: 0px;
											font-size: ${index === 0 ? "20px" : "inherit"};
											line-height: ${index === 0 ? "24px" : "20px"};
											letter-spacing: ${index === 0 ? "-1px" : "0"};
										`}
									>
										{item.title}
									</Typography>
								</Link>
								<Typography
									variant="div"
									size="sm"
									type="primary"
									color={theme.colors.primary.gray}
									css={css`
										position: absolute;
										left: 20px;
										bottom: 17px;
									`}
								>
									{FormatDate(item.publishDate)}
								</Typography>
							</MobileNewsItem>
						);
					})}
					<Spacer
						css={css`
							height: 20px;
						`}
					/>
				</RelativeContentSection>
				<StayTunedComponent parent={stayTunedParent} parentCategory={filter} />
				<ContentSection type="primary" vPadding="48px">
					{currentNews.slice(6, mobileSlicePoint).map((item, index: number) => {
						return (
							<React.Fragment key={index}>
								<MobileNewsItem
									key={index}
									css={css`
										background-color: ${theme.colors.secondary.background};
									`}
								>
									<Badge
										css={css`
											margin-bottom: 25px;
										`}
										type="primary"
										color={NewsBadgeColor(item.category as NewsCategories)}
									>
										{MultiLingualNewsCategories[language][item.categorySlug]}
									</Badge>
									<Link
										to={
											"/about-us/news-events/news/" +
											encodeURI(item.categorySlug) +
											"/" +
											encodeURI(item.slug)
										}
									>
										<Typography
											variant="div"
											weight="bold"
											color={theme.colors.primary.header}
											size="lg"
											css={css`
												margin-top: 0px;
												margin-bottom: 0px;
											`}
										>
											{item.title}
										</Typography>
									</Link>
									<Typography
										variant="div"
										size="sm"
										type="primary"
										color={theme.colors.primary.gray}
										css={css`
											position: absolute;
											left: 20px;
											bottom: 17px;
										`}
									>
										{FormatDate(item.publishDate)}
									</Typography>
								</MobileNewsItem>
								{(index + 1) % 4 === 0 ? (
									<>
										<Spacer
											css={css`
												height: 40px;
											`}
										/>
									</>
								) : (
									<></>
								)}
							</React.Fragment>
						);
					})}
					{/* `mobileSlicePoint` cards are showing; as on desktop, one card left gets
					no button. */}
					{currentNews.length - 1 > mobileSlicePoint ? (
						<LoadMore
							css={css`
								border: 1px solid ${theme.colors.primary.gray};
							`}
							onClick={() => {
								setMobileSlicePoint(mobileSlicePoint + 4);
							}}
						>
							<Typography
								variant="span"
								color={theme.colors.primary.darkGray}
								weight="bold"
								size="sm"
							>
								{content.loadMore}
							</Typography>
						</LoadMore>
					) : (
						<></>
					)}
					<Spacer
						css={css`
							height: 60px;
						`}
					/>
				</ContentSection>
			</Media>
		</>
	);
};

export default AllNews;
